import { useEffect, useRef } from "react";

// Each open sheet pushes a uniquely-tagged history entry so we can tell, on
// cleanup, whether our entry is still on top of the stack.
let sheetCounter = 0;

/**
 * While `isOpen`, captures the browser/native back button so it closes the
 * sheet instead of navigating the page away.
 *
 * On open we push a throwaway history entry; pressing back (browser button,
 * Android hardware back, or the Flutter shell's back which calls
 * `history.back()`) pops it and fires `onClose`. Closing the sheet any other
 * way (Escape, backdrop, "Done") removes that entry on cleanup so the history
 * stack stays balanced and a later real back press still works.
 */
export function useBackButtonClose(isOpen: boolean, onClose: () => void) {
  // Keep the latest onClose in a ref so the effect only re-runs on isOpen —
  // a new onClose identity must NOT pop+push the history entry.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;

    const id = ++sheetCounter;
    window.history.pushState({ __sheet: id }, "");

    const onPop = () => onCloseRef.current();
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
      // If our entry is still on top, the sheet closed by some other means
      // (Escape/backdrop/Done) — pop it so we don't leave a dangling entry.
      // If back was pressed, the browser already popped it and this is false.
      if (window.history.state?.__sheet === id) {
        window.history.back();
      }
    };
  }, [isOpen]);
}
